import { useCallback } from "react";
import { API_ENDPOINTS } from "../config/constants";

export const COMMENT_CONSTANTS = {
  INITIAL_PAGE: 1,
  PAGE_SIZE: 10,
  MAX_DEPTH: 3,
  LOADING_STATES: {
    IDLE: "idle",
    LOADING: "loading",
    ERROR: "error",
    SUCCESS: "success",
  },
};

export const commentUtils = {
  formatDate: (date) => new Date(date).toLocaleString(),
  shouldShowReplies: (comment) =>
    comment.replyCount > 0 || comment.replies?.length > 0,
  isCommentOwner: (user, comment) => user?._id === comment.user?._id,
};

export const useDiscussionApi = () => {
  const fetchReplies = useCallback(async (commentId, page = 1) => {
    try {
      const res = await fetch(
        `${API_ENDPOINTS.DISCUSSIONS}/${commentId}/replies?page=${page}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to fetch replies");
      return await res.json();
    } catch (err) {
      console.error("Error fetching replies:", err);
      throw err;
    }
  }, []);

  const createComment = useCallback(async (vehicleId, text) => {
    try {
      const res = await fetch(API_ENDPOINTS.DISCUSSIONS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ vehicleId, comment: text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Failed to create comment");
      return data;
    } catch (err) {
      console.error("Error creating comment:", err);
      throw err;
    }
  }, []);

  const submitReply = useCallback(async (commentId, text) => {
    try {
      const res = await fetch(
        `${API_ENDPOINTS.DISCUSSIONS}/${commentId}/reply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ comment: text }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Failed to submit reply");
      return data;
    } catch (err) {
      console.error("Error submitting reply:", err);
      throw err;
    }
  }, []);

  const editComment = useCallback(async (commentId, text) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/${commentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ comment: text }),
      });
      if (!res.ok) throw new Error("Failed to edit comment");
      return await res.json();
    } catch (err) {
      console.error("Error editing comment:", err);
      throw err;
    }
  }, []);

  const deleteComment = useCallback(async (commentId) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/${commentId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete comment");
      return await res.json();
    } catch (err) {
      console.error("Error deleting comment:", err);
      throw err;
    }
  }, []);

  const reportComment = useCallback(async (commentId, reason) => {
    try {
      const res = await fetch(API_ENDPOINTS.REPORT_COMMENT(commentId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error("Failed to report comment");
      return await res.json();
    } catch (err) {
      console.error("Error reporting comment:", err);
      throw err;
    }
  }, []);

  return {
    fetchReplies,
    createComment,
    submitReply,
    editComment,
    deleteComment,
    reportComment,
  };
};
